import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth, db } from "../FirebaseConfig";
import { toast, ToastContainer } from "react-toastify";
import { doc, getDoc } from "firebase/firestore";
import { Eye, EyeOff } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

const MentorLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const navigate = useNavigate();

  const resetForm = useCallback(() => {
    setForgotPassword(false);
    setResetEmail("");
    setResetError("");
    setResetSuccess("");
    setIsSubmitting(false);
  }, []);

  const handleLogin = useCallback(
    async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setResetError("");
      setResetSuccess("");

      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const userDocRef = doc(db, "mentors", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const storedUserData = {
            uid: user.uid,
            name: userData.name,
            email: userData.email,
            phone: userData.phone,
            role: "mentor",
          };
          localStorage.setItem("user", JSON.stringify(storedUserData));
          toast.success("Logged in successfully!");
          navigate("/profile");
        } else {
          toast.error("Mentor data not found! Please register.");
          navigate("/mentor-register");
        }
      } catch (error) {
        if (error.code === "auth/wrong-password") {
          setResetError("Incorrect password. Please try again.");
        } else if (error.code === "auth/user-not-found") {
          setResetError("No account found with this email.");
        } else {
          setResetError("Invalid email or password.");
        }
        toast.error(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, navigate]
  );

  const handleResetPassword = useCallback(
    async (e) => {
      e.preventDefault();
      setResetError("");
      setResetSuccess("");
      setIsSubmitting(true);

      if (!resetEmail) {
        setResetError("Please enter your email.");
        setIsSubmitting(false);
        return;
      }

      try {
        await sendPasswordResetEmail(auth, resetEmail);
        setResetSuccess("Password reset email sent! Check your inbox (and spam folder).");
        toast.success("Password reset email sent!");
        setTimeout(() => {
          resetForm();
        }, 3000);
      } catch (error) {
        if (error.code === "auth/user-not-found") {
          setResetError("No account found with this email.");
        } else {
          setResetError("Failed to send reset email. Please try again.");
        }
        toast.error("Failed to send reset email.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [resetEmail, resetForm]
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-4xl bg-white shadow-2xl rounded-lg overflow-hidden md:flex">
        <div className="md:w-1/2 p-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src={require("../assets/images/logo.jpg")}
              alt="Project Portal Logo"
              className="h-20"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            {forgotPassword ? "Reset Password" : "Mentor Log In"}
          </h2>
          {!forgotPassword ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setForgotPassword(true)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
              <button
                type="submit"
                className={`w-full py-3 rounded-lg text-white font-semibold ${
                  isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-700"
                } transition-colors`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Logging In..." : "Log In"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                <p>
                  <strong>Note:</strong> A password reset link will be sent to your email. Follow the instructions in the email to reset your password.
                </p>
              </div>
              {resetError && <p className="text-red-500 text-sm">{resetError}</p>}
              {resetSuccess && <p className="text-green-500 text-sm">{resetSuccess}</p>}
              <button
                type="submit"
                className={`w-full py-3 rounded-lg text-white font-semibold ${
                  isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-700"
                } transition-colors`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Send Reset Email"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="w-full text-sm text-blue-600 hover:underline"
              >
                Back to Login
              </button>
            </form>
          )}
          {!forgotPassword && (
            <div className="mt-4 text-center">
              <p className="text-sm">
                Don't have an account?{" "}
                <Link to="/mentor-register" className="text-blue-600 hover:underline font-semibold">
                  Sign Up
                </Link>
              </p>
            </div>
          )}
        </div>
        <div className="md:w-1/2 p-8 bg-gray-50">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Enjoying productivity and insights with Project Portal?
          </h3>
          <p className="mb-4">
            <span className="text-green-600 font-bold">Yes!</span> Contact us about adding your other
            projects to Project Portal.
          </p>
          <p>
            I have feedback.{" "}
            <a href="#" className="text-blue-600 font-semibold hover:underline">
              Share
            </a>{" "}
            your ideas and feedback with us.
          </p>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default MentorLogin;